<template>
	<div class="chart-wrapper lcpCard">
		<div class="fund-total"><span class="inputTitle">Funds:</span> {{visiblePoints.length}}</div>
		<highcharts class="chartWrapper" :options="chartOptions" ref="highcharts" :key="updated"/>
		<div class="text-wrapper">
			Unless otherwise stated, returns of a non-Sterling investment are shown on an unhedged currency basis.  Currency movements can have a positive or negative impact on local market returns. Figures shown refer to the past and past performance is not a reliable indicator of current or future performance.
		</div>
	</div>
</template>

<script>
import Highcharts from 'highcharts/highcharts';
import More from 'highcharts/highcharts-more';
import Vue from 'vue';
import _ from 'lodash';
import histogram from 'highcharts/modules/histogram-bellcurve';
import { Chart } from 'highcharts-vue';
import { ChartOptions, GeneralTooltip, InfastructureTooltip } from '@/components/charts/chartSettings.js';

More(Highcharts);
Vue.use(_);
histogram(Highcharts);

Highcharts.setOptions(ChartOptions);

class DataSeriesPoint {
    constructor (x, y, z, color, info, tooltip) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.color = color;
        this.info = info;
        this.tooltip = tooltip;
    }
}

export default {
    components: {
        highcharts: Chart,
    },
    data () {
        return {
            chartOptions: {
                chart: {},
                xAxis: {
                    title: {
						text: '',
						style: {
							fontSize: '20px'
						}
					},
                    labels: {
                    },
                },
                yAxis: [
					{
						title: {
							text: '',
							style: {
								fontSize: '20px'
							}
						},
						labels: {
                        }
					}
				],
                tooltip: {
                    enabled: true,
                    formatter: null,
                },
                plotOptions: {
                    histogram: {},
                },
            },
            defaultChartOptions: {
                chart: {},
                xAxis: {
                    title: {
						text: '',
						style: {
							fontSize: '20px'
						}
					},
                    labels: {
                        style: {
							fontSize: '17px'
						}
                    },
                },
                yAxis: [
					{
						title: {
							text: '',
							style: {
								fontSize: '20px'
							}
						},
						labels: {
                            style: {
							fontSize: '17px'
						}
                        }
					}
				],
                tooltip: {
                    enabled: true,
                    formatter: null,
                },
                plotOptions: {
                    histogram: {},
                },
            },
            filters: {},
            updated: 0,
        };
    },
    props: {
        returnVal: {
            type: Array,
        },
    },
    created () {
        this.setupChart();
    },
    mounted () {
        window.addEventListener('resize', this.resize());
        this.setupChart();
    },
    beforeDestroy () {
        window.removeEventListener('resize', this.resize());
    },
    methods: {
        resize () {
            this.$children[0].chart.setSize();
        },
        setMinMax (minX, minY, maxX, maxY) {
            if (minX > 0) this.chartOptions.xAxis.min = 0;
            if (minY > 0) this.chartOptions.yAxis[0].min = 0;

            // this.chartOptions.xAxis.max = maxX;
            // this.chartOptions.yAxis[0].max = maxY;
        },
        removeMinMax () {
            delete this.chartOptions.xAxis.min;
            delete this.chartOptions.yAxis.min;
            // delete this.chartOptions.xAxis.max;
            // delete this.chartOptions.yAxis.max;
        },
        setupHistogram (chartData) {
            this.chartOptions.xAxis = [
                {
                    title: { text: null },
                    alignTicks: false,
                },
                {
                    title: { text: 'Fees' },
                    alignTicks: false,
                },
            ];

            this.chartOptions.yAxis = [
                {
                    title: { text: null },
                },
                {
                    title: { text: 'Frequency' },
                },
            ];
            this.chartOptions.series = [
                {
                    name: 'Histogram',
                    type: 'histogram',
                    xAxis: 1,
                    yAxis: 1,
                    baseSeries: 's1',
                    zIndex: -1,
                },
                {
                    name: 'Data',
                    type: 'scatter',
                    data: chartData.map(i => ({ y: i.Fee, info: i })).filter(z => z.y !== null),
                    id: 's1',
                    marker: {
                        radius: 1.5,
                    },
                    visible: false,
                },
            ];
        },
        setupColumn (chartData) {
            const newChartData = [];
            this.chartOptions.tooltip.enabled = false;
            this.chartOptions.xAxis.type = 'category';
            this.chartOptions.yAxis[0].title.text = 'Funds';
            if (this.getXaxisSelection === 'Complexity') {
                let Simple = 0;
                let Medium = 0;
                let Complex = 0;
                let Unrated = 0;
                for (let point = 0; point < chartData.length; point += 1) {
                    const fundIndex = this.$store.state.universe.data.allMetaData.findIndex(x => x.Guid === chartData[point].id);
                    if (fundIndex > -1) {
                        switch (this.$store.state.universe.data.allMetaData[fundIndex].Complexity) {
                        case 'Simple':
                            Simple += 1;
                            break;
                        case 'Medium':
                            Medium += 1;
                            break;
                        case 'Complex':
                            Complex += 1;
                            break;
                        case 'Unrated':
                            Unrated += 1;
                            break;
                        default:
                            break;
                        }
                    }
                }
                newChartData.push(['Simple', Simple]);
                newChartData.push(['Medium', Medium]);
                newChartData.push(['Complex', Complex]);
                newChartData.push(['Unrated', Unrated]);
                this.chartOptions.series.push({ data: newChartData, name: this.getXaxisSelection });
            } else {
                const indexCat = this.$store.state.universe.data.categoryFilter.findIndex(cat => cat.categoryName === this.getXaxisSelection);
                if (indexCat > -1) {
                    for (let c = 0; c < this.$store.state.universe.data.categoryFilter[indexCat].orderNames.length; c += 1) {
                        let count = 0;
                        for (let point = 0; point < chartData.length; point += 1) {
                            if (
                                chartData[point][this.getXaxisSelection]
								=== this.$store.state.universe.data.categoryFilter[indexCat].orderNames[c]
                            ) {
                                count += 1;
                            }
                        }
                        newChartData.push([this.$store.state.universe.data.categoryFilter[indexCat].orderNames[c], count]);
                    }
                    this.chartOptions.series.push({ data: newChartData, name: this.getXaxisSelection });
                }
            }
        },
        setupStandard (chartData) {
            const newChartData = [];
            const totalY = [];
            const totalX = [];

            const hiddenPoints = [];
            let colour = '';
            const index = this.universeData.categoryDimensions.findIndex(i => i.name === this.$store.state.chart.colourSelection);
            const category = this.universeData.categoryDimensions[index];

            this.chartOptions.tooltip.enabled = true;
            this.chartOptions.xAxis.hasOwnProperty('type') ? delete this.chartOptions.xAxis.type : '';

            for (let point = 0; point < chartData.length; point += 1) {
                let returnValue = true;
                const dataPoint = chartData[point];

                if (this.$store.state.ui.showSelectedData) {
                    if (
                        this.$store.state.circleDataToStore.find(x => x.id === dataPoint.id)
                    ) {
                        return true;
                    }
                    return false;
                }

                for (let j = 0; j < this.$store.state.categoryFilter.length; j += 1) {
                    if (
                        this.$store.state.categoryFilter[j]
						&& this.$store.state.categoryFilter[j].selectedOrder.length > 0
						&& this.$store.state.categoryFilter[j].selectedOrder.indexOf(dataPoint[this.$store.state.categoryFilter[j].categoryName])
							=== -1
                    ) returnValue = false;
                }

                for (let k = 0; k < this.getValueFilter.length; k += 1) {
                    if (dataPoint[this.getValueFilter[k].valueName] !== undefined) {
                        const value = dataPoint[this.getValueFilter[k].valueName];
                        if (this.getValueFilter[k].values[0] !== this.getValueFilter[k].min) {
                            if (value < this.getValueFilter[k].values[0]) {
                                returnValue = false;
                            }
                        }
                        if (this.getValueFilter[k].values[1] !== this.getValueFilter[k].max && returnValue) {
                            if (value > this.getValueFilter[k].values[1]) {
                                returnValue = false;
                            }
                        }
                    } else {
                        returnValue = false;
                    }
                }

                if (this.$store.state.universe.dueDilligence) {
                    returnValue = dataPoint.LcpDueDilligence !== 'Pass' ? false : returnValue;
                }
                if (this.$store.state.universe.openFundsOnly) {
                    returnValue = dataPoint.FundOpenOrClosed !== 'Yes' ? false : returnValue;
                }

                if (chartData[point][this.getXaxisSelection] !== null && chartData[point][this.getYaxisSelection] !== null) {
                    totalY.push(chartData[point][this.getYaxisSelection]);
                    totalX.push(chartData[point][this.getXaxisSelection]);
                } else {
                    returnValue = false;
                }

                if (returnValue) {
                    const pf = this.$store.state.user.pinnedFunds.findIndex(p => p === chartData[point].ticker);
                    let altColor = '';

                    if (this.$store.state.chart.colourSelection === 'Pinned Funds') {
                        altColor = 'rgb(0,163,199)';
                    } else {
                        const colIndex = category.order.findIndex(l => l === chartData[point][this.$store.state.chart.colourSelection]);
                        altColor = category.colors[colIndex];
                    }

                    colour = pf > -1 ? '#F20505' : altColor;

                    if (this.getYaxisSelection === 'bin') {
                        newChartData.push(chartData[point][this.getXaxisSelection]);
                    } else {
                        newChartData.push(
                            new DataSeriesPoint(
                                chartData[point][this.getXaxisSelection],
                                chartData[point][this.getYaxisSelection],
                                chartData[point][this.getRaxisSelection] !== null
                                    ? parseInt(chartData[point][this.getRaxisSelection].toFixed(2))
                                    : 0,
                                colour,
                                chartData[point],
                                true,
                            ),
                        );
                    }
                } else {
                    hiddenPoints.push(
                        new DataSeriesPoint(
                            chartData[point][this.getXaxisSelection],
                            chartData[point][this.getYaxisSelection],
                            chartData[point][this.getRaxisSelection] !== null ? parseInt(chartData[point][this.getRaxisSelection].toFixed(2)) : 0,
                            'rgba(230,230,230,0.1)',
                            chartData[point],
                            false,
                        ),
                    );
                }
            }

            if (this.chartType === 'scatter' || this.chartType === 'bubble') {
                this.setMinMax(Math.min(...totalX), Math.min(...totalY), Math.max(...totalX), Math.max(...totalY));
            } else {
                this.removeMinMax();
            }

            this.$store.commit('chart/updateVisiblePoints', newChartData);

            if (this.getYaxisSelection === 'bin') {
                this.chartOptions.series = [
                    {
                        type: 'histogram',
                        xAxis: 1,
                        yAxis: 1,
                        baseSeries: 1,
                    },
                    {
                        data: newChartData,
                    },
                ];
            } else {
                this.chartOptions.series.push({ name: 'Inactive', data: hiddenPoints }, { name: 'Active', data: newChartData });
            }

            if (this.getYaxisSelection === 'AUM') {
                this.chartOptions.yAxis[0].labels.formatter = function () {
                    return `${this.value / 1000}bn`;
                };
            } else if (this.getYaxisSelection === 'Length of track record') {
                this.chartOptions.yAxis[0].labels.formatter = function () {
                    return `${this.value} years`;
                };
            } else if (this.getYaxisSelection === 'Fee' || this.getXaxisSelection === 'Return (% - pa over 1y)' || this.getXaxisSelection === 'Standard deviation (% pa)') {
                this.chartOptions.yAxis[0].labels.formatter = function () {
                    return `${this.value}%`;
                };
            } else if (this.getXaxisSelection !== 'Fee') {
                this.chartOptions.yAxis[0].labels.formatter = null;
            }

            if (this.getXaxisSelection === 'AUM') {
                this.chartOptions.xAxis.labels.formatter = function () {
                    return `${this.value / 1000}bn`;
                };
            } else if (this.getXaxisSelection === 'Length of track record') {
                this.chartOptions.xAxis.labels.formatter = function () {
                    return `${this.value} yrs`;
                };
            } else if ((this.getXaxisSelection === 'Fee' && this.chartOptions.chart.type !== 'histogram') || this.getXaxisSelection === 'Return (% - pa over 1y)' || this.getXaxisSelection === 'Standard deviation (% pa)') {
                this.chartOptions.xAxis.labels.formatter = function () {
                    return `${this.value}%`;
                };
            } else if (this.getXaxisSelection !== 'Fee') {
                this.chartOptions.xAxis.labels.formatter = null;
            }
        },
        setupChart () {
            this.updated += 1;
            const data = _.cloneDeep(this.universeData);

            this.chartOptions = _.cloneDeep(this.defaultChartOptions);
            this.chartOptions.chart.type = this.chartType;
            this.chartOptions.xAxis.title.text = this.getXaxisSelection;
            this.chartOptions.yAxis[0].title.text = this.getYaxisSelection;
            this.chartOptions.tooltip.formatter = this.tooltipSelection === 'Infrastructure' ? InfastructureTooltip : GeneralTooltip;
            this.chartOptions.series = [];

            for (let v = 0; v < data.valueDimensions.length; v += 1) {
                const valueDimension = data.valueDimensions[v];
                data[valueDimension.name] = valueDimension;
                for (let j = 0; j < data.data.length; j += 1) {
                    if (data.data[j].values[valueDimension.index] !== null && data.data[j].values[valueDimension.index] !== undefined) {
                        data.data[j][valueDimension.name] = data.data[j].values[valueDimension.index] * valueDimension.valueScaling.scaleFactor;
                    } else {
                        data.data[j][valueDimension.name] = null;
                    }
                }
                valueDimension.totalScaling.scaleFactor /= valueDimension.valueScaling.scaleFactor;
                valueDimension.valueScaling.scaleFactor = 1;
            }

            for (var point = 0; point < data.data.length; point += 1) {
                const fundIndex = this.$store.state.universe.data.allMetaData.findIndex(x => x.Guid === data.data[point].id);
                if (fundIndex > -1) {
                    data.data[point].FundOpenOrClosed = this.$store.state.universe.data.allMetaData[fundIndex].FundOpenOrClosed;
                    data.data[point].LcpDueDilligence = this.$store.state.universe.data.allMetaData[fundIndex].LcpDueDilligence;
                }
            }

            for (let i = 0; i < data.categoryDimensions.length; i += 1) {
                const categoryDimension = data.categoryDimensions[i];
                data[categoryDimension.name] = categoryDimension;
                for (let z = 0; z < data.data.length; z += 1) {
                    data.data[z][categoryDimension.name] = data.data[z].categories[categoryDimension.index];
                    data.data[z].noData = false;
                }
            }

            const chartData = [...data.data];

            if (this.chartType === 'column') {
                this.setupColumn(chartData);
            } else if (this.chartType === 'histogram') {
                this.setupHistogram(chartData);
            } else {
                this.setupStandard(chartData);
            }
        },
    },
    computed: {
        fullscreen () {
            return this.$store.state.ui.fullScreen;
        },
        colourSelection () {
            return this.$store.state.chart.colourSelection;
        },
        managerData () {
            return this.$store.getters.getUnDeletedSelectedMangers;
        },
        dimensions () {
            return this.$store.state.chart.dimensions;
        },
        PensionsData () {
            return this.$store.state.pensionsData;
        },
        universeData () {
            return this.$store.state.universe.data;
        },
        visiblePoints () {
            return this.$store.state.chart.visiblePoints;
        },
        getRaxisSelection () {
            return this.$store.getters.getRaxisSelection;
        },
        getXaxisSelection () {
            return this.$store.getters.getXaxisSelection;
        },
        getYaxisSelection () {
            return this.$store.getters.getYaxisSelection;
        },
        getCategoryFilter () {
            return this.$store.getters.getCategoryFilter;
        },
        getValueFilter () {
            return this.$store.state.valueFilter;
        },
        chartType () {
            return this.$store.state.chart.type;
        },
        tooltipSelection () {
            return this.$store.state.chart.tooltipSelection;
        },
        pinnedFunds () {
            return this.$store.state.user.pinnedFunds;
        },
    },
    watch: {
        fullscreen () {
            this.resize();
        },
        colourSelection () {
            this.setupChart();
        },
        managerData () {
            this.setupChart();
        },
        dimensions () {
            this.setupChart();
        },
        PensionsData () {
            this.setupChart();
        },
        universeData () {
            this.setupChart();
        },
        getRaxisSelection () {
            this.setupChart();
        },
        getXaxisSelection () {
            this.setupChart();
        },
        getYaxisSelection () {
            this.setupChart();
        },
        getCategoryFilter: {
			handler () {
				this.setupChart();
			},
			deep: true,
        },
        getValueFilter: {
            handler () {
                this.setupChart();
            },
            deep: true,
        },
        chartType () {
            this.setupChart();
        },
        tooltipSelection () {
            this.setupChart();
        },
        pinnedFunds () {
            this.setupChart();
        },
    },
};
</script>

<style lang="scss">
.chartWrapper {
	height: 96%;
}
.comparisonChart {
	height: 100vh;
	#performanceChart {
		padding: 20px;
		.axis line {
			fill: none;
			stroke: #000;
			shape-rendering: crispEdges;
		}
		.x.axis path {
			display: none;
		}
		.line {
			fill: none;
			stroke: steelblue;
			stroke-width: 2px;
		}
	}
}
.chart-wrapper {
	height: 100%;
	padding-top: 20px;
	background: #fff;
}
.text-wrapper {
	color: rgb(141, 168, 173);
	font-size: 12px;
	height: 4%;
	padding: 0 10px;
}
</style>
